import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { DollarSign, Users, Award, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DonorboxWidget() {
  const containerRef = useRef(null);

  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://donorbox.org/widgets.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://donorbox.org/widgets.js';
      script.type = 'module';
      script.async = true;
      document.body.appendChild(script);
    }

    if (containerRef.current && !containerRef.current.querySelector('dbox-widget')) {
      const widget = document.createElement('dbox-widget');
      widget.setAttribute('campaign', 'goodliving-scholars-scholarship-937803');
      widget.setAttribute('type', 'donation_form');
      widget.setAttribute('enable-auto-scroll', 'true');
      containerRef.current.appendChild(widget);
    }
  }, []);

  return <div ref={containerRef} data-testid="donorbox-widget-portal" />;
}

export default function DonorPortal() {
  const { user, updateRole } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/donor/metrics`, { withCredentials: true });
      setMetrics(res.data);
    } catch (err) {
      console.error('Failed to load metrics:', err.message);
      toast.error('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const switchToStudent = async () => {
    await updateRole('student');
    toast.success('Switched to Student view');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cabernet border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="donor-portal">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-medium text-cabernet tracking-tight">
              Donor Portal
            </h1>
            <p className="font-body text-sm text-ink/60 mt-1">
              Thank you for supporting our mission, {user?.name || 'Donor'}
            </p>
          </div>
          <Button
            onClick={switchToStudent}
            variant="outline"
            className="border-2 border-cabernet text-cabernet hover:bg-cabernet/5 font-accent text-sm"
            data-testid="switch-to-student-btn"
          >
            Switch to Student View
          </Button>
        </div>

        {/* Impact Metrics */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="donor-metrics">
          {[
            {
              icon: DollarSign,
              value: `$${(metrics?.total_donated || 0).toLocaleString()}`,
              label: 'Total Donated',
            },
            {
              icon: Award,
              value: metrics?.scholarships_funded || 0,
              label: 'Scholarships Funded',
            },
            {
              icon: Users,
              value: metrics?.students_impacted || 0,
              label: 'Students Impacted',
            },
            {
              icon: TrendingUp,
              value: '100%',
              label: 'Funds to Students',
            },
          ].map((metric) => (
            <div key={metric.label} className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-6">
              <metric.icon className="w-6 h-6 text-brass mb-3" />
              <p className="text-2xl font-heading font-semibold text-cabernet">{metric.value}</p>
              <p className="font-accent text-xs text-ink/60 mt-1">{metric.label}</p>
            </div>
          ))}
        </div>

        {/* Recent Activity + Donorbox */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Recent Donations */}
          <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-6">
            <h2 className="text-xl font-heading font-medium text-cabernet mb-4">Recent Contributions</h2>
            <div className="space-y-3">
              {(metrics?.recent_donations || []).map((donation, i) => (
                <div key={`${donation.donor_name}-${donation.created_at}-${i}`} className="flex items-center justify-between py-3 border-b border-brass/10 last:border-0">
                  <div>
                    <p className="font-accent text-sm font-medium text-ink">
                      {donation.donor_name || 'Anonymous'}
                    </p>
                    <p className="font-body text-xs text-ink/50">
                      {new Date(donation.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="font-heading text-lg font-semibold text-cabernet">
                    ${(donation.amount || 0).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Donorbox Embed */}
          <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-6" data-testid="donorbox-embed-container">
            <h2 className="text-xl font-heading font-medium text-cabernet mb-4">Make a Donation</h2>
            <div className="rounded-lg overflow-hidden border border-brass/30 shadow-md p-4">
              <DonorboxWidget />
            </div>
            <p className="font-body text-xs text-ink/50 mt-3 text-center">
              Powered by Donorbox. All donations are tax-deductible.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
